import React, {
  useState, useEffect, useRef, useCallback,
  useMemo,
} from "react";
import moment from "moment";
import { Button, Tooltip } from "antd";
import { toast } from "react-toastify";
import { getDelayedReports } from "../../../actions/reportDelay";
import useAbortController from "../../Common/hooks/useAbortController";
import Error from "../../Error";
import ReportDelayFilters from "./ReportDelayFilters";
import ReportDelayFormPopup from "./ReportDelayFormPopup";
import ReportDelayHistory from "./ReportDelayHistory";
import ReportDelayTable from "./ReportDelayTable";
import { formatAntdDateRange, getUser } from "../../../utils/helpers";
import { defaultDateRange, resolveReportDelayStatus } from "./utils";
import "./ReportDelay.css";

const columns = [
  {
    title: "WO ID",
    dataIndex: "workorder_id",
    key: "workorder_id",
    width: 120,
  },
  {
    title: "Genista ID",
    dataIndex: "genista_id",
    key: "genista_id",
    width: 150,
  },
  {
    title: "Test",
    dataIndex: "test_name",
    key: "test_name",
    width: 200,
  },
  {
    title: "Test Category",
    dataIndex: "category_name",
    key: "category_name",
    width: 200,
  },
  {
    title: "Result Due By",
    dataIndex: "current_eta",
    key: "current_eta",
    width: 120,
    render: (value) => (value ? moment(value).format("MM/DD/YYYY") : ""),
  },
  {
    title: "Expected ETA",
    dataIndex: "expected_eta",
    key: "expected_eta",
    width: 120,
    render: (value, record) => {
      const updatedAt = record.override_created_at || null;
      const createdBy = record.override_created_by_name || null;
      return (
        <Tooltip
          title={(
            <div>
              {createdBy && <div>{`Reported By: ${createdBy}`}</div>}
              {updatedAt && <div>{`Reported At: ${updatedAt}`}</div>}
            </div>
          )}
          overlayClassName="ReportDelay__ExpectedETA_Tooltip"
          placement="topLeft"
        >
          <span>{value ? moment(value).format("MM/DD/YYYY") : "-"}</span>
        </Tooltip>
      );
    },
  },
  {
    title: "Reason",
    dataIndex: "reason",
    key: "reason",
    ellipsis: true,
    width: 220,
    render: (value) => value || "-",
  },
  {
    title: "Comment",
    dataIndex: "comments",
    key: "comments",
    ellipsis: true,
    width: 220,
    render: (value) => value || "-",
  },
  {
    title: "Test Status",
    dataIndex: "status",
    key: "status",
    width: 100,
    render: (value) => {
      if (!value) return "-";
      const cfg = resolveReportDelayStatus(value);
      if (!cfg) return value ?? "-";
      return <span style={{ color: cfg.style?.color }}>{cfg.label}</span>;
    },
  },
];

const defaultRangeUnformatted = defaultDateRange();
const defaultWODateRange = defaultRangeUnformatted ? formatAntdDateRange(defaultRangeUnformatted) : null;
const source = "test_eta_override";

export default function ReportDelay() {
  const [showError, setShowError] = useState({ show: false, message: "" });
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const { setNewAbortController, clearAbortController, abortController } = useAbortController();
  const isMounted = useRef(true);
  const containerRef = useRef(null);
  const [tableWidth, setTableWidth] = useState(1000);

  const user = useMemo(() => getUser(), []);
  const canAddReportDelay = user.permissionMap.add_report_delay;

  const [filters, setFilters] = useState(() => ({
    source,
    date_range: defaultWODateRange,
    status: "pending",
    user_id: null,
    reason: null,
    test_category_id: null,
    search: null,
  }));
  const [isReportDelayOpen, setIsReportDelayOpen] = useState(false);
  const [historyModalOpen, setHistoryModalOpen] = useState(false);
  const [historyRecord, setHistoryRecord] = useState(null);

  const handleFiltersChange = useCallback((nextFilters) => {
    setFilters(nextFilters);
  }, []);

  const fetchEtaHistory = useCallback(async (activeFilters) => {
    const newAbortController = setNewAbortController();
    setData([]);
    setLoading(true);

    const params = {
      source,
      role_user_id: user.id,
      ...(activeFilters || {}),
    };

    try {
      const res = await getDelayedReports(params, newAbortController.signal);
      if (!isMounted.current) return;
      // Drop stale responses (a newer request has superseded this one)
      if (newAbortController !== abortController.current) return;

      if (res.success) {
        const rows = res.results ?? [];
        setData(rows);
      } else if (!res.canceled) {
        toast.error(res.message || "Failed to load ETA history");
        setData([]);
      }
    } finally {
      const isLatest = newAbortController === abortController.current;
      if (isMounted.current && isLatest) {
        setLoading(false);
      }
      if (isLatest) {
        clearAbortController();
      }
    }
  }, [setNewAbortController, clearAbortController, abortController]); // eslint-disable-line

  useEffect(() => {
    fetchEtaHistory(filters);
  }, [filters]); // eslint-disable-line

  useEffect(() => {
    const computeTableWidth = () => {
      const el = containerRef.current;
      if (!el) return;
      const cs = window.getComputedStyle(el);
      const padX = (parseFloat(cs.paddingLeft) || 0) + (parseFloat(cs.paddingRight) || 0);
      const inner = Math.max(0, el.clientWidth - padX);
      setTableWidth(inner);
    };
    computeTableWidth();
    window.addEventListener("resize", computeTableWidth);
    return () => window.removeEventListener("resize", computeTableWidth);
  }, []);

  const handleCancel = (isUpdated) => {
    setIsReportDelayOpen(false);
    if (isUpdated) {
      fetchEtaHistory(filters);
    }
  };

  const toggleError = (error) => {
    setShowError(error);
  };

  const handleHistoryModalClose = useCallback(() => {
    setHistoryModalOpen(false);
    setHistoryRecord(null);
  }, []);

  const handleTableRowClick = useCallback((record) => ({
    onClick: () => {
      setHistoryRecord(record);
      setHistoryModalOpen(true);
    },
    style: { cursor: "pointer" },
  }), []);

  return (
    <div className="ReportDelayContainer" ref={containerRef}>
      <div className="ReportDelayHeaderRow">
        <ReportDelayFilters
          onFiltersChange={handleFiltersChange}
          initialFilters={{
            search: filters.search,
            dateRange: defaultRangeUnformatted,
            status: filters.status,
            selectedCategory: "",
            selectedAssignTo: "",
            reason: "",
          }}
        />
        {canAddReportDelay && (
          <div className="ReportDelayHeaderRow__Actions">
            <Button type="primary" onClick={() => setIsReportDelayOpen(true)}>
              Report Delay
            </Button>
          </div>
        )}
      </div>
      <div style={{ width: tableWidth }}>
        <ReportDelayTable
          rowKey="tests_in_sample_pk"
          dataSource={data}
          columns={columns}
          loading={loading}
          scroll={{ x: "100%", y: "60vh" }}
          onRow={handleTableRowClick}
          pagination
        />
      </div>
      {isReportDelayOpen && (
        <ReportDelayFormPopup
          open={isReportDelayOpen}
          onCancel={handleCancel}
        />
      )}
      {historyModalOpen && (
        <ReportDelayHistory
          open={historyModalOpen}
          onCancel={handleHistoryModalClose}
          record={historyRecord}
        />
      )}
      {showError.show && (
        <Error message={showError.message} toggleError={toggleError} />
      )}
    </div>
  );
}
